import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Tag, Sparkles, CalendarRange } from 'lucide-react';
import api from '../../services/apiClient';
import useAuthStore from '../../store/authStore';
import { Button, Field, Input, Textarea } from '../ui';
import { toast } from '../toast';
import { NETWORKS, NETWORK_LABELS, NETWORK_ICONS, NETWORK_COLORS } from '../../lib/networks';

const SUGGESTED = ['Dicas', 'Bastidores', 'Promoções', 'Depoimentos', 'Novidades'];

// Gera vários posts de uma vez, alternando entre os "temas" (pilares no backend).
export default function MultiPost() {
  const currentCompany = useAuthStore((s) => s.currentCompany);
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [loading, setLoading] = useState(true);

  const [networks, setNetworks] = useState(['instagram', 'facebook']);
  const [count, setCount] = useState(8);
  const [postsPerWeek, setPostsPerWeek] = useState(3);
  const [hour, setHour] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [tone, setTone] = useState('');
  const [running, setRunning] = useState(false);

  const companyId = currentCompany?.id;

  const load = async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await api.get('/ai/pillars');
      setThemes(data.items || []);
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const addTheme = async (name, description = '') => {
    if (!name.trim()) return;
    try {
      await api.post('/ai/pillars', { name: name.trim(), description: description.trim() || undefined });
      setNewName(''); setNewDesc('');
      await load();
    } catch (err) { toast.error(err.message); }
  };

  const removeTheme = async (id) => {
    try { await api.del(`/ai/pillars/${id}`); await load(); }
    catch (err) { toast.error(err.message); }
  };

  const toggleNet = (n) => setNetworks((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));

  const run = async () => {
    if (!startDate) return toast.error('Escolha a data para começar');
    if (networks.length === 0) return toast.error('Selecione ao menos uma rede');
    setRunning(true);
    try {
      const res = await api.post('/ai/generate-batch', {
        networks,
        count: Number(count),
        postsPerWeek: Number(postsPerWeek),
        hour: Number(hour),
        startDate: new Date(startDate).toISOString(),
        tone: tone || undefined
      });
      toast.success(`${res.count} posts gerados como rascunho!`);
      navigate('/calendario');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRunning(false);
    }
  };

  if (!companyId) return <p className="muted">Selecione uma empresa no topo.</p>;

  const activeThemes = themes.filter((t) => t.active).length;
  const maxByNetworks = networks.length ? Math.floor(60 / networks.length) : 0;
  const missingThemes = themes.length > 0 ? [] : SUGGESTED;

  return (
    <div>
      <p className="muted" style={{ marginTop: 0, maxWidth: 720 }}>
        Crie vários posts de uma vez. A IA escreve tudo como <strong>rascunho</strong>, alternando entre os <strong>temas</strong> que
        você definir e já distribuindo nas datas. Depois é só revisar e agendar no Calendário.
      </p>

      <div className="grid cols-2">
        {/* Temas */}
        <div className="card">
          <h3 style={{ marginTop: 0 }}><Tag size={17} /> Temas de conteúdo</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Assuntos que a IA vai alternar entre os posts. Ex: Dicas, Bastidores, Promoções.
          </p>

          {loading ? <p className="muted">Carregando…</p> : (
            <>
              {themes.length > 0 ? (
                <div className="grid" style={{ gap: 6 }}>
                  {themes.map((t) => (
                    <div key={t.id} className="row between" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <strong>{t.name}</strong>
                        {t.description && <div className="muted" style={{ fontSize: 13 }}>{t.description}</div>}
                      </div>
                      <Button variant="ghost" className="sm" onClick={() => removeTheme(t.id)}><Trash2 size={14} /></Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="row wrap mb8">
                  <span className="muted" style={{ fontSize: 13 }}>Sugestões rápidas:</span>
                  {missingThemes.map((s) => (
                    <span key={s} className="net-chip" onClick={() => addTheme(s)}><Plus size={13} /> {s}</span>
                  ))}
                </div>
              )}

              <div className="mt16">
                <Field label="Novo tema">
                  <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nome (ex: Dicas)" />
                </Field>
                <Field hint="Opcional: oriente a IA sobre o que abordar neste tema">
                  <Textarea rows={2} value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                    placeholder="Ex: dicas práticas de uso do produto, em tom leve" />
                </Field>
                <Button variant="secondary" onClick={() => addTheme(newName, newDesc)}><Plus size={15} /> Adicionar tema</Button>
              </div>
            </>
          )}
        </div>

        {/* Configuração */}
        <div className="card">
          <h3 style={{ marginTop: 0 }}><CalendarRange size={17} /> Quantos e quando</h3>
          <p className="muted" style={{ marginTop: 0 }}>{activeThemes} tema(s) ativo(s).</p>

          <Field label="Redes">
            <div className="row wrap">
              {NETWORKS.map((n) => {
                const Icon = NETWORK_ICONS[n];
                const on = networks.includes(n);
                return (
                  <span key={n} className={`net-chip ${on ? 'on' : ''}`} onClick={() => toggleNet(n)}>
                    <Icon size={14} color={on ? NETWORK_COLORS[n] : '#9aa3af'} /> {NETWORK_LABELS[n]}
                  </span>
                );
              })}
            </div>
          </Field>

          <div className="grid cols-2">
            <Field label={`Quantos posts (até ${maxByNetworks})`}>
              <Input type="number" min={1} max={maxByNetworks || 30} value={count} onChange={(e) => setCount(e.target.value)} />
            </Field>
            <Field label="Posts por semana">
              <Input type="number" min={1} max={14} value={postsPerWeek} onChange={(e) => setPostsPerWeek(e.target.value)} />
            </Field>
            <Field label="Começar em">
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label="Horário">
              <Input type="number" min={0} max={23} value={hour} onChange={(e) => setHour(e.target.value)} />
            </Field>
          </div>

          <Field label="Tom (opcional)">
            <Input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Ex: leve e inspirador" />
          </Field>

          <Button onClick={run} loading={running} disabled={activeThemes === 0}>
            <Sparkles size={16} /> Gerar {count} posts
          </Button>
          {activeThemes === 0 && <p className="hint">Adicione ao menos um tema para gerar.</p>}
        </div>
      </div>
    </div>
  );
}
